import { useRef, useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CellWithTooltipProps {
  children: React.ReactNode;
  content?: string | React.ReactNode;
}

export function CellWithTooltip({ children, content }: CellWithTooltipProps) {
  const cellRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (cellRef.current) {
        const { scrollWidth, clientWidth } = cellRef.current;
        setIsTruncated(scrollWidth > clientWidth);
      }
    };

    checkTruncation();
    // Check again after a short delay to account for any async rendering
    const timeout = setTimeout(checkTruncation, 100);

    // Also check on window resize
    window.addEventListener("resize", checkTruncation);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [children]);

  // Use provided content or convert children to string for tooltip
  const tooltipContent = content ?? (typeof children === "string" ? children : String(children));

  // If content is truncated, wrap in tooltip
  if (isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={cellRef}
            className="overflow-hidden text-ellipsis whitespace-nowrap w-full"
          >
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-md break-words">{tooltipContent}</div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // If not truncated, just render normally
  return (
    <div
      ref={cellRef}
      className="overflow-hidden text-ellipsis whitespace-nowrap w-full"
    >
      {children}
    </div>
  );
}

